import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Footer from "@/components/Footer";
import { toast } from "sonner";

const LOGO = process.env.PUBLIC_URL + "/nextrota-logo.png";

function validate(p) {
  if (p.length < 8) return "Use at least 8 characters.";
  if (!/[A-Z]/.test(p)) return "Include an uppercase letter.";
  if (!/[a-z]/.test(p)) return "Include a lowercase letter.";
  if (!/[0-9]/.test(p)) return "Include a number.";
  return null;
}

export default function AcceptInvitePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const token = params.get("token") || "";
  const [info, setInfo] = useState(null);
  const [infoError, setInfoError] = useState(null);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setInfoError("Missing token."); return; }
    api.get(`/auth/invite-info?token=${encodeURIComponent(token)}`)
      .then(r => setInfo(r.data))
      .catch(e => setInfoError(formatError(e.response?.data?.detail) || "Invalid invitation link."));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    const err = validate(pw);
    if (err) return toast.error(err);
    if (pw !== pw2) return toast.error("Passwords don't match.");
    setBusy(true);
    try {
      await api.post("/auth/accept-invite", { token, password: pw });
      await refresh();
      toast.success(`Welcome to NextRota, ${info?.name?.split(" ")[0] || ""}!`);
      navigate("/app");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F6F9FE]">
      <main className="flex-1 grid place-items-center px-6 py-12">
        <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 p-8 sm:p-10 shadow-sm">
          <img src={LOGO} alt="NextRota" className="h-8 w-auto mb-6"/>

          {infoError ? (
            <>
              <div className="eyebrow mb-2">Invitation</div>
              <h1 className="font-display text-3xl mb-3 text-[#0B1A36]">Link can't be used</h1>
              <p className="text-sm text-red-600 mb-4" data-testid="invite-error">{infoError}</p>
              <p className="text-sm text-[#4B5773]">Ask your admin to send a fresh invitation, or <Link to="/login" className="text-[#2563EB] underline">sign in</Link> if you already have an account.</p>
            </>
          ) : !info ? (
            <p className="text-sm text-[#4B5773]" data-testid="invite-loading">Checking your invitation…</p>
          ) : (
            <>
              <div className="eyebrow mb-2">You're invited</div>
              <h1 className="font-display text-3xl mb-2 text-[#0B1A36]">Join {info.company_name}</h1>
              <p className="text-sm text-[#4B5773] mb-6">Set a password for <strong>{info.email}</strong> to activate your NextRota account. This link expires in 60 minutes.</p>
              <form onSubmit={submit} className="space-y-4" data-testid="invite-form">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#4B5773]">Create password</Label>
                  <Input required type="password" value={pw} data-testid="invite-pw"
                    onChange={(e)=>setPw(e.target.value)}
                    className="h-11 mt-2 rounded-lg border-slate-200"/>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#4B5773]">Confirm password</Label>
                  <Input required type="password" value={pw2} data-testid="invite-pw2"
                    onChange={(e)=>setPw2(e.target.value)}
                    className="h-11 mt-2 rounded-lg border-slate-200"/>
                </div>
                <p className="text-xs text-[#4B5773]">Use at least 8 characters, including a lowercase letter, an uppercase letter, a number and a special character such as ! @ # $ % ? or *.</p>
                <Button type="submit" disabled={busy} data-testid="invite-submit"
                  className="w-full h-11 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
                  {busy ? "Activating…" : "Activate account"}
                </Button>
                <p className="text-[10px] text-[#4B5773] text-center">
                  By continuing you agree to our <Link to="/terms-of-service" className="underline">Terms</Link> and <Link to="/privacy-policy" className="underline">Privacy Policy</Link>.
                </p>
              </form>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
